const KNOWN_CODES = [
  "ACCOUNT_EXISTS",
  "ADMIN_ACCOUNT_EXISTS",
  "ACCOUNT_CONVERTED",
  "ACCOUNT_SUSPENDED",
  "DISPOSABLE_EMAIL",
  "SIGNUP_TOKEN_INVALID",
  "INVALID_ANSWERS",
  "SHORTLIST_FULL",
  "ALREADY_SHORTLISTED",
  "ALREADY_REQUESTED",
  "EMPTY_SHORTLIST",
  "PREFERENCES_REQUIRED",
  "PROFILE_REDACTED",
  "CAPTCHA_FAILED",
  "TOO_MANY_ATTEMPTS",
  "MAGIC_LINK_INVALID",
  "MAGIC_LINK_WRONG_DEVICE",
  "HUMAN_CHECK_REQUIRED",
  "PLG_PROOF_INVALID",
  "COUNTRY_NOT_SUPPORTED",
  "VIEW_LIMIT_SESSION",
  "VIEW_LIMIT_DAILY",
  "VIEW_LIMIT_ACCOUNT",
];

/**
 * The portal backend wraps errors as `{ error: [{ code?, description }] }`
 * (error.handler.middleware.ts). New PRD-3334 endpoints set `code`; we also
 * accept a top-level `code` and a known code at the start of `description` so
 * the UI keeps working if a proxied marketplace error is forwarded verbatim.
 */
export function extractErrorCode(payload: unknown): string | undefined {
  if (!payload || typeof payload !== "object") return undefined;
  const p = payload as { code?: unknown; error?: Array<{ code?: unknown; description?: unknown }> };
  const first = Array.isArray(p.error) ? p.error[0] : undefined;
  if (first && typeof first.code === "string") return first.code;
  if (typeof p.code === "string") return p.code;
  const description = first && typeof first.description === "string" ? first.description : "";
  return KNOWN_CODES.find((code) => description.startsWith(code));
}

export function extractErrorMessage(payload: unknown): string | undefined {
  if (!payload || typeof payload !== "object") return undefined;
  const first = (payload as { error?: Array<{ description?: unknown }> }).error?.[0];
  return typeof first?.description === "string" ? first.description : undefined;
}

export type ApiResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; code?: string; message?: string };

export function toApiResult<T>(response: { ok: boolean; status: number; payload: unknown }): ApiResult<T> {
  if (response?.ok) return { ok: true, status: response.status, data: response.payload as T };
  return {
    ok: false,
    status: response?.status ?? 0,
    code: extractErrorCode(response?.payload),
    message: extractErrorMessage(response?.payload),
  };
}
